import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { navLinks } from "../data/mockData";
import { ArrowRight, ArrowUpRight, Menu, X, Sparkles } from "lucide-react";
import { useBookDemo } from "../hooks/useBookDemo";

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const navBookBtnRef = useRef(null);
  const { openBookDemo } = useBookDemo();

  // Lock button width on mount
  useEffect(() => {
    if (navBookBtnRef.current) {
      navBookBtnRef.current.style.width = `${navBookBtnRef.current.offsetWidth}px`;
    }
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const isActive = (href) => {
    // Only treat true route paths (starting with "/") as active. Hash links never highlight.
    if (!href || !href.startsWith("/")) return false;
    return location.pathname === href;
  };

  // Handles nav links that point to a hash on the homepage (e.g. "/#platform-section").
  // If already on "/", smooth-scroll to the element. Otherwise, navigate to home
  // then scroll after a short delay so the section is mounted.
  const handleHashLink = (e, href) => {
    if (!href || !href.includes("#")) return;
    e.preventDefault();
    const [pathPart, hashPart] = href.split("#");
    const targetPath = pathPart || "/";
    const scrollToId = () => {
      const el = document.getElementById(hashPart);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    if (location.pathname === targetPath) {
      scrollToId();
    } else {
      navigate(targetPath);
      setTimeout(scrollToId, 250);
    }
    setMobileMenuOpen(false);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white border-b border-gray-200 ${
        isScrolled ? "shadow-sm" : ""
      }`}
    >
      <div className="max-w-[1280px] mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-[72px]">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group flex-shrink-0">
            <img 
              src="https://customer-assets.emergentagent.com/job_laughing-sinoussi-3/artifacts/thym66qi_image.png"
              alt="Elevante Logo"
              className="h-8 w-auto"
            />
          </Link>

          {/* Desktop Nav Links — centered */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => {
              const active = isActive(link.href);
              const baseCls = `text-[15px] transition-colors duration-200 ${
                active
                  ? "text-[#0F0F1E] font-bold"
                  : "text-gray-500 hover:text-gray-900 font-medium"
              }`;
              const isHashLink = link.href.includes("#");
              if (isHashLink) {
                return (
                  <a
                    key={link.label}
                    href={link.href}
                    onClick={(e) => handleHashLink(e, link.href)}
                    className={baseCls}
                    data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {link.label}
                  </a>
                );
              }
              return link.href.startsWith("/") ? (
                <Link
                  key={link.label}
                  to={link.href}
                  className={baseCls}
                  data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {link.label}
                </Link>
              ) : (
                <a
                  key={link.label}
                  href={link.href}
                  className={baseCls}
                  data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {link.label}
                </a>
              );
            })}
          </div>

          {/* CTA Button — smaller size for navbar */}
          <div className="hidden lg:block flex-shrink-0">
            <a
              ref={navBookBtnRef}
              href="#demo"
              onClick={openBookDemo}
              className="book-demo-btn inline-flex items-center rounded-lg border-[1.5px] border-black overflow-hidden transition-shadow duration-200 shadow-sm hover:shadow-md h-[46px]"
            >
              <span className="book-demo-purple relative z-10 flex items-center gap-2 bg-[#8942FE] text-white text-[14px] font-bold tracking-wide h-full py-0 px-5">
                <span className="book-demo-dot w-2.5 h-2.5 rounded-sm bg-[#10B981] flex-shrink-0" />
                Book a demo
              </span>
              <span className="book-demo-arrow flex items-center justify-center bg-white border-l-[1.5px] border-black h-full py-0 px-3">
                <ArrowRight className="w-4 h-4 text-black" strokeWidth={2.5} />
              </span>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-gray-700 hover:text-gray-900"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-300 ${
          mobileMenuOpen ? "max-h-[400px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="px-6 py-4 bg-white border-t border-gray-100">
          {navLinks.map((link) => {
            const active = isActive(link.href);
            // On hover/tap: purple text, purple underline, external arrow icon appears
            const baseCls = `group block py-3 text-[15px] font-medium no-underline w-fit transition-colors duration-200 hover:text-[#8B5CF6] active:text-[#8B5CF6] focus:text-[#8B5CF6] ${
              active ? "text-[#0F0F1E] font-bold" : "text-gray-500"
            }`;
            const tapStyle = {
              WebkitTapHighlightColor: "transparent",
              outline: "none",
            };
            const inner = (
              <span className="flex items-center gap-1.5">
                <span className="relative">
                  {link.label}
                  {/* Underline appears on hover/tap */}
                  <span className="absolute left-0 right-0 -bottom-0.5 h-[1.5px] bg-[#8B5CF6] origin-left scale-x-0 group-hover:scale-x-100 group-active:scale-x-100 group-focus:scale-x-100 transition-transform duration-300 ease-out" />
                </span>
                <ArrowUpRight
                  className="w-4 h-4 text-[#8B5CF6] opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 group-active:opacity-100 group-active:translate-x-0 group-focus:opacity-100 group-focus:translate-x-0 transition-all duration-300 ease-out"
                  strokeWidth={2.2}
                />
              </span>
            );
            return link.href.startsWith("/") && !link.href.includes("#") ? (
              <Link
                key={link.label}
                to={link.href}
                className={baseCls}
                style={tapStyle}
                onClick={() => setMobileMenuOpen(false)}
              >
                {inner}
              </Link>
            ) : (
              <a
                key={link.label}
                href={link.href}
                className={baseCls}
                style={tapStyle}
                onClick={(e) => {
                  if (link.href.includes("#")) handleHashLink(e, link.href);
                  setMobileMenuOpen(false);
                }}
              >
                {inner}
              </a>
            );
          })}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
